import React, { useState } from 'react';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Size } from '@atlaskit/icon';
import Button from '@atlaskit/button';
import Popup from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { Y200 } from '@atlaskit/theme/colors';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { QuoteV2 } from '@atlassian/cssxp-types-quote';

interface QuoteWarningIconProps {
  size?: Size;
  titleContent?: React.ReactNode;
  showStaleReasonAtHeaderLevel?: boolean;
  showStaleQuoteReasonAtListLevel?: boolean;
}

const contentStyles = xcss({
  padding: 'space.150',
  maxWidth: '400px',
});

export const QuoteDetailsPopup = ({
  size,
  titleContent,
  showStaleReasonAtHeaderLevel = false,
  showStaleQuoteReasonAtListLevel = false,
}: QuoteWarningIconProps) => {
  const [isOpen, setIsOpen] = useState(false);
  let timeoutId: any;

  const defaultReasonAtHeaderLevel =
    'One or more items on this quote need review. Hover over the line item warnings or select relevant line items for more info.';
  const defaultReasonAtListLevel = 'Action required. Open the quote for more details.';

  const getFallbackReason = () => {
    if (showStaleReasonAtHeaderLevel) return defaultReasonAtHeaderLevel;
    else if (showStaleQuoteReasonAtListLevel) return defaultReasonAtListLevel;
    else return sentenceCase(QuoteV2.QuoteStatus.NEEDS_REVIEW);
  };

  return (
    <div className="quote-details-popup">
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom"
        content={() => (
          <Box
            xcss={contentStyles}
            onMouseLeave={() => {
              clearTimeout(timeoutId);
              setIsOpen(false);
            }}
            onMouseEnter={() => {
              clearTimeout(timeoutId);
              setIsOpen(true);
            }}
            testId="quote-details-popup-content"
          >
            {titleContent ?? getFallbackReason()}
          </Box>
        )}
        testId="quote-details-popup"
        trigger={(triggerProps) => (
          <Button
            {...triggerProps}
            appearance="subtle"
            isSelected={isOpen}
            onMouseEnter={() => {
              clearTimeout(timeoutId);
              setIsOpen(true);
            }}
            onMouseLeave={() => {
              clearTimeout(timeoutId);
              timeoutId = setTimeout(() => setIsOpen(false), 100);
            }}
            className="stale-status stale-reason-container"
            testId="stale-quote-warning-icon"
          >
            <WarningIcon label="" primaryColor={Y200} size={size} />
          </Button>
        )}
      />
    </div>
  );
};
